import {
  DEFAULT_PRE_ANNOTATE_CONFIG,
  hasUsableDescriptions,
  Label,
  LabelDescriptions,
  PreAnnotateAvailability,
  PreAnnotateModelTypeEnum,
  PreAnnotateProviderEnum,
  PreAnnotateSettingsDocument,
  UpdatePreAnnotateSettingsDocument,
} from "@repo/schema";
import type { I18nKey } from "@/core/i18n";
import { SAVE_BLOCKED_DESCRIBE, VISION_UNAVAILABLE } from "./copy";

/**
 * Pure form model for the auto-annotate settings dialog.
 *
 * Everything the dialog can change lives here, and every rule about it — what
 * "dirty" means, what Save needs, how the state becomes the PUT body — is a
 * plain function of this state. Robopipe Vision is the only engine, so there
 * is no engine or model to pick: the form is the default mode plus the label
 * descriptions. The React hook next door only wires it to `useReducer`, so the
 * rules can be reasoned about (and, when a web test runner arrives, tested)
 * without rendering anything.
 */

/** The editable part of the form — what a Save persists. */
export interface PreAnnotateFormFields {
  defaultMode: PreAnnotateModelTypeEnum;
  /**
   * Carried through from the GET unchanged. The reference-image picker is not
   * rendered this pass, but a Save must not null out what an earlier version
   * stored, so the value rides along in the PUT verbatim.
   */
  referenceTaskId: number | null;
  /** labelId (stringified) -> description, as typed; trimmed only on save. */
  labelDescriptions: LabelDescriptions;
}

export interface PreAnnotateFormState extends PreAnnotateFormFields {
  /** Snapshot taken by the last `init`; dirty = fields differ from this. */
  initial: PreAnnotateFormFields;
  saveError: string | null;
}

export type PreAnnotateFormAction =
  | {
      type: "init";
      settings: PreAnnotateSettingsDocument | undefined;
      /**
       * True when the dialog has just opened: overwrite whatever the previous
       * session left behind (Cancel does not clear the form). False for a
       * refetch while open, where a dirty form must be left alone.
       */
      force: boolean;
    }
  | { type: "setDefaultMode"; mode: PreAnnotateModelTypeEnum }
  | { type: "setDescription"; labelId: number; value: string }
  | { type: "saveFailed"; message: string }
  | { type: "clearError" };

/**
 * Vision detects boxes only for now, so its default mode is not a choice.
 * Applied wherever the mode is set or seeded — including the `initial`
 * snapshot, so opening the dialog on a legacy segmentation row does not
 * present itself as an unsaved edit. The one place to open up once Vision
 * can segment.
 */
const coerceDefaultMode = (
  _mode: PreAnnotateModelTypeEnum,
): PreAnnotateModelTypeEnum => PreAnnotateModelTypeEnum.DETECTION;

// ─── Init ─────────────────────────────────────────────────────────────────────

const fieldsFromDocument = (
  settings: PreAnnotateSettingsDocument | undefined,
): PreAnnotateFormFields => {
  if (!settings) {
    return {
      defaultMode: DEFAULT_PRE_ANNOTATE_CONFIG.defaultMode,
      referenceTaskId: DEFAULT_PRE_ANNOTATE_CONFIG.referenceTaskId,
      labelDescriptions: {},
    };
  }
  return {
    defaultMode: coerceDefaultMode(settings.config.defaultMode),
    referenceTaskId: settings.config.referenceTaskId,
    labelDescriptions: { ...settings.labelDescriptions },
  };
};

export const initialPreAnnotateFormState = (): PreAnnotateFormState => {
  const fields = fieldsFromDocument(undefined);
  return { ...fields, initial: fields, saveError: null };
};

// ─── Dirty check ──────────────────────────────────────────────────────────────

/**
 * Blank and absent mean the same thing (no description), so typing into a
 * field and clearing it again is not an edit.
 */
const nonBlankDescriptions = (d: LabelDescriptions): Map<string, string> =>
  new Map(
    Object.entries(d)
      .map(([k, v]) => [k, v.trim()] as const)
      .filter(([, v]) => v.length > 0),
  );

const sameDescriptions = (a: LabelDescriptions, b: LabelDescriptions): boolean => {
  const ma = nonBlankDescriptions(a);
  const mb = nonBlankDescriptions(b);
  if (ma.size !== mb.size) return false;
  for (const [k, v] of ma) if (mb.get(k) !== v) return false;
  return true;
};

export const isPreAnnotateFormDirty = (state: PreAnnotateFormState): boolean => {
  const i = state.initial;
  return (
    state.defaultMode !== i.defaultMode ||
    state.referenceTaskId !== i.referenceTaskId ||
    !sameDescriptions(state.labelDescriptions, i.labelDescriptions)
  );
};

// ─── Reducer ──────────────────────────────────────────────────────────────────

export function preAnnotateSettingsFormReducer(
  state: PreAnnotateFormState,
  action: PreAnnotateFormAction,
): PreAnnotateFormState {
  switch (action.type) {
    case "init": {
      // Saving the settings invalidates the label tag, and editing a label
      // invalidates the settings tag, so the document refetches while the
      // dialog is open. Re-seeding a form the user is mid-edit would throw
      // their changes away; only a clean form follows the server.
      if (!action.force && isPreAnnotateFormDirty(state)) return state;
      const fields = fieldsFromDocument(action.settings);
      return { ...fields, initial: fields, saveError: null };
    }
    case "setDefaultMode": {
      // The toggle is disabled under Vision, but a stray dispatch must not be
      // able to persist a mode the API would refuse.
      const mode = coerceDefaultMode(action.mode);
      return mode === state.defaultMode
        ? state
        : { ...state, defaultMode: mode, saveError: null };
    }
    case "setDescription":
      return {
        ...state,
        labelDescriptions: {
          ...state.labelDescriptions,
          [String(action.labelId)]: action.value,
        },
      };
    case "saveFailed":
      return { ...state, saveError: action.message };
    case "clearError":
      return state.saveError === null ? state : { ...state, saveError: null };
  }
}

// ─── Derived ──────────────────────────────────────────────────────────────────

/** Only `visionAvailable` (the provider's credentials on the server) matters. */
export const isVisionAvailable = (
  availability: PreAnnotateAvailability | undefined,
): boolean => availability?.visionAvailable === true;

/**
 * The PUT body. Descriptions carry every project label — "" is how a cleared
 * description reaches the API.
 */
export const toUpdateDocument = (
  state: PreAnnotateFormFields,
  projectLabels: readonly Pick<Label, "id">[],
): UpdatePreAnnotateSettingsDocument => ({
  config: {
    provider: PreAnnotateProviderEnum.ROBOPIPE_VISION,
    defaultMode: state.defaultMode,
    referenceTaskId: state.referenceTaskId,
  },
  labelDescriptions: Object.fromEntries(
    projectLabels.map((l) => [
      String(l.id),
      (state.labelDescriptions[String(l.id)] ?? "").trim(),
    ]),
  ),
});

export interface PreAnnotateSaveGate {
  ok: boolean;
  /** Why Save is disabled — shown as its tooltip. Absent while merely busy. */
  reason?: I18nKey<"label">;
}

/**
 * What Robopipe Vision needs before its settings are worth saving: a
 * configured server plus at least one described label. A clean form may
 * still be saved.
 */
export const preAnnotateSaveGate = (
  state: PreAnnotateFormFields,
  availability: PreAnnotateAvailability | undefined,
  busy: boolean,
): PreAnnotateSaveGate => {
  if (busy) return { ok: false };
  if (!isVisionAvailable(availability)) {
    return { ok: false, reason: VISION_UNAVAILABLE };
  }
  if (!hasUsableDescriptions(state.labelDescriptions)) {
    return { ok: false, reason: SAVE_BLOCKED_DESCRIBE };
  }
  return { ok: true };
};
